import { getAvailableResult } from "DependencyResult";
import type {
  DependencyAccessMixedValue,
  DependencyArgument,
  DependencyVisitorContext,
} from "DependencyVisitorContext";
import { decoratedWaitAvailableOne, operationTypes } from "DependencyVisitorUtils";
import { DependencyError } from "Errors";

const decorationInfo = Object.freeze({
  operationType: operationTypes.Boolean,
  operandCount: 1,
  booleanResult: true,
});

function and<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  return decoratedWaitAvailableOne(
    left,
    data,
    context,
    (left) => {
      return left !== true ? getAvailableResult(left) : right(data, context);
    },
    decorationInfo,
  );
}

function coalesce<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  return decoratedWaitAvailableOne(
    value,
    data,
    context,
    (value) => {
      if (typeof value === "boolean") {
        return getAvailableResult(value);
      } else if (value == null) {
        return getAvailableResult(false);
      } else {
        throw new DependencyError("Encountered a boolean expression that evaluated to a non-boolean value.");
      }
    },
    decorationInfo,
  );
}

function not<Mode>(
  value: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  return decoratedWaitAvailableOne(
    value,
    data,
    context,
    (value) => {
      if (value != null && typeof value !== "boolean") {
        throw new DependencyError("! operator can only be used on a boolean value.");
      } else {
        return getAvailableResult(!value);
      }
    },
    decorationInfo,
  );
}

function or<Mode>(
  left: DependencyArgument,
  right: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  return decoratedWaitAvailableOne(
    left,
    data,
    context,
    (left) => {
      if (left !== false && left != null) {
        return getAvailableResult(left);
      } else {
        return right(data, context);
      }
    },
    decorationInfo,
  );
}

export default { and, coalesce, not, or };
